import React, { useEffect, useState } from 'react';
import { Check, ExternalLink, Images, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StepIntro } from '../fields';
import { templateDemoUrl, templateImageUrl, wget } from '../api';

export function TemplateGallery({ template, onClose }) {
  if (!template) return null;
  const demo = template.demo ? templateDemoUrl(template.id) : template.preview_url;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ww-template-gallery">
        <DialogHeader><DialogTitle>{template.name}</DialogTitle></DialogHeader>
        {template.description && <p className="text-sm text-slate-600">{template.description}</p>}
        {demo && (
          <a href={demo} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-teal-700 font-semibold" data-testid="ww-template-preview-link"><ExternalLink className="w-4 h-4" /> {template.demo ? 'Open the working demo in a new tab' : 'Open live preview'}</a>
        )}
        {template.demo && (
          <div className="hidden md:block rounded-lg border border-slate-200 overflow-hidden bg-slate-100" data-testid="ww-template-demo-frame">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-200 text-[11px] text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-rose-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /><span className="ml-2 truncate">Working demo — click around</span></div>
            <iframe src={demo} title={`${template.name} demo`} className="w-full h-[520px] bg-white" sandbox="allow-scripts allow-same-origin" />
          </div>
        )}
        <div className="space-y-3">
          {(template.images || []).map((img) => <img key={img.id} src={templateImageUrl(template.id, img.id)} alt={img.filename} className="w-full rounded-lg border border-slate-200" />)}
          {!(template.images || []).length && !template.demo && <p className="text-sm text-slate-400 italic">No screenshots yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TemplateCard({ t, selected, onPreview, onSelect, compact }) {
  const cover = t.images?.[0];
  return (
    <div className={`rounded-2xl border-2 bg-white overflow-hidden transition-shadow ${selected ? 'border-teal-600 shadow-md' : 'border-slate-200 hover:shadow-sm'}`} data-testid={`ww-template-${t.id}`} data-selected={selected || undefined}>
      <button type="button" onClick={onPreview} className="block w-full aspect-[4/3] bg-slate-100 relative">
        {cover ? <img src={templateImageUrl(t.id, cover.id)} alt={t.name} className="w-full h-full object-cover object-top" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-slate-400"><Images className="w-10 h-10" /></div>}
        {selected && <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded-full"><Check className="w-3 h-3" /> Selected</span>}
        {t.demo && <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-slate-900/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`ww-template-demo-badge-${t.id}`}><MousePointerClick className="w-3 h-3" /> Working demo</span>}
      </button>
      <div className="p-3">
        <div className="font-bold text-slate-900">{t.name}</div>
        {!compact && t.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>}
        <div className="flex gap-2 mt-3">
          {t.demo ? <Button asChild variant="outline" size="sm" className="flex-1"><a href={templateDemoUrl(t.id)} target="_blank" rel="noreferrer" data-testid={`ww-template-preview-${t.id}`}>Preview Website</a></Button>
            : <Button type="button" variant="outline" size="sm" className="flex-1" onClick={onPreview} data-testid={`ww-template-preview-${t.id}`}>Preview Website</Button>}
          {onSelect && <Button type="button" size="sm" className={`flex-1 ${selected ? 'bg-teal-700' : 'bg-slate-900'} text-white`} onClick={onSelect} data-testid={`ww-template-select-${t.id}`}>{selected ? 'Selected' : 'Select Website'}</Button>}
        </div>
        {t.demo && <button type="button" onClick={onPreview} className="mt-2 text-[11px] text-slate-500 hover:text-slate-800 underline" data-testid={`ww-template-screens-${t.id}`}>See screenshots</button>}
      </div>
    </div>
  );
}

export default function StepTemplate({ templateId, onSelect }) {
  const [templates, setTemplates] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => { wget('/website/templates').then(setTemplates).catch(() => setTemplates([])); }, []);
  return (
    <div>
      <StepIntro title="Choose your website" text="Pick the design you like best — we'll build it with your branding, services and photos. Tap a card to see full screenshots." />
      {templates === null && <p className="text-sm text-slate-500">Loading designs…</p>}
      {templates?.length === 0 && <p className="text-sm text-slate-500 italic" data-testid="ww-no-templates">No designs are available yet — check back soon.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(templates || []).map((t) => <TemplateCard key={t.id} t={t} selected={templateId === t.id} onPreview={() => setPreview(t)} onSelect={() => onSelect(t.id)} />)}
      </div>
      <TemplateGallery template={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
